import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Configuration } from '@rbx/clients';
import { useRobloxAuthentication as useAuthentication } from '@rbx/auth';
import { GroupsApi, ListGroupResponse, GroupListSurface } from '@rbx/clients/creatorHomeApi';
import { getBEDEV2ServiceBasePathV2 } from '../utils/getBasePaths';
import { EventContext, TrackerClientRequest } from '../event/eventConstants';
import useNavigationConfigs from '../hooks/useNavigationConfigs';

async function compareAndLogDifference(
  newApiResponsePromise: Promise<ListGroupResponse>,
  oldApiResponsePromise: Promise<ListGroupResponse>,
  sendEvent: (clientRequest: TrackerClientRequest) => void,
  surface: GroupListSurface,
  userId: number,
): Promise<void> {
  const [newResult, oldResult] = await Promise.allSettled([
    newApiResponsePromise,
    oldApiResponsePromise,
  ]);
  if (oldResult.status === 'rejected') {
    sendEvent({
      eventType: 'ReverseDelegationShadow_GroupOldFetchError',
      context: EventContext.Error,
      parameters: {
        surface,
        userId: `${userId}`,
        error: oldResult.reason,
      },
    });
  }
  if (newResult.status === 'rejected') {
    sendEvent({
      eventType: 'ReverseDelegationShadow_GroupNewFetchError',
      context: EventContext.Error,
      parameters: {
        surface,
        userId: `${userId}`,
        error: newResult.reason,
      },
    });
  }
  
  if (oldResult.status === 'rejected' || newResult.status === 'rejected') {
    // do not compare if any API failed
    return;
  }

  const newIds = newResult.value.groups.map((g) => g.id);
  const oldIds = oldResult.value.groups.map((g) => g.id);
  const oldIdSet = new Set(oldIds);
  const newIdSet = new Set(newIds);
  const missingInNew = oldIds.filter((id) => !newIdSet.has(id));
  const extraInNew = newIds.filter((id) => !oldIdSet.has(id));

  if (missingInNew.length > 0) {
    sendEvent({
      eventType: 'ReverseDelegationShadow_GroupsMissingInNew',
      context: EventContext.Error,
      parameters: {
        surface,
        userId: `${userId ?? ''}`,
        missingGroupIds: missingInNew.join(','),
        missingCount: `${missingInNew.length}`,
        oldCount: `${oldIds.length}`,
        newCount: `${newIds.length}`,
      },
    });
  }
  if (extraInNew.length > 0) {
    sendEvent({
      eventType: 'ReverseDelegationShadow_GroupsMissingInOld',
      context: EventContext.Error,
      parameters: {
        surface,
        userId: `${userId ?? ''}`,
        extraGroupIds: extraInNew.join(','),
        extraCount: `${extraInNew.length}`,
        oldCount: `${oldIds.length}`,
        newCount: `${newIds.length}`,
      },
    });
  }

  if (missingInNew.length === 0 && extraInNew.length === 0) {
    // response match!
    sendEvent({
      eventType: 'ReverseDelegationShadow_GroupsMatch',
      context: EventContext.Impression,
      parameters: {
        surface,
        userId: `${userId ?? ''}`,
        groupCount: `${oldIds.length}`,
      },
    });
  }
}

const useGetGroupsList = () => {
  const {
    target,
    robloxEnvironment: environment,
    enableReverseDelegation,
    enableReverseDelegationShadow,
    sendEvent,
  } = useNavigationConfigs();
  const { user } = useAuthentication();
  const groupsApi = useMemo(() => {
    const basePath = getBEDEV2ServiceBasePathV2('creator-home-api', target, environment);

    const configuration = new Configuration({
      basePath,
      credentials: 'include',
    });

    return new GroupsApi(configuration);
  }, [environment, target]);

  return useQuery({
    queryKey: ['creatorHome', 'groupList', enableReverseDelegation, enableReverseDelegationShadow],
    enabled: !!user,
    queryFn: () => {
      let newResponse: Promise<ListGroupResponse> | undefined;
      let oldResponse: Promise<ListGroupResponse> | undefined;
      if (enableReverseDelegationShadow || enableReverseDelegation) {
        newResponse = groupsApi.groupsListGroups({
          surface: GroupListSurface.CreatorHub,
        });
      }
      if (enableReverseDelegationShadow || !enableReverseDelegation) {
        oldResponse = groupsApi.groupsListGroups();
      }

      if (enableReverseDelegationShadow) {
        compareAndLogDifference(
          newResponse!,
          oldResponse!,
          sendEvent,
          GroupListSurface.CreatorHub,
          user?.id ?? 0,
        );
      }
      if (enableReverseDelegation) {
        return newResponse;
      }
      return oldResponse;
    },
  });
};

export default useGetGroupsList;
